import React, { useState, useMemo } from 'react';

function MonthlyCalendar({ bets, displayMode = 'dollars', unitSize = 25, getUnitSizeForDate, advancedUnitSettings = false }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [hoveredDay, setHoveredDay] = useState(null);
    const [hoveredMonth, setHoveredMonth] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'yearly'

    // Get the current month and year
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // Format value based on display mode (for pre-calculated unit values)
    const formatValueUnits = (value, showPrefix = true) => {
        if (value === undefined || value === null) return '';
        const prefix = showPrefix ? (value >= 0 ? '+' : '-') : '';
        return `${prefix}${Math.abs(value).toFixed(1)}U`;
    };

    // Format value based on display mode (for dollar values that need conversion)
    const formatValue = (value, showPrefix = true) => {
        if (value === undefined || value === null) return '';
        if (displayMode === 'units') {
            const units = value / unitSize;
            const prefix = showPrefix ? (units >= 0 ? '+' : '-') : '';
            return `${prefix}${Math.abs(units).toFixed(1)}U`;
        }
        const prefix = showPrefix ? (value >= 0 ? '+$' : '-$') : '$';
        return `${prefix}${Math.abs(value).toFixed(2)}`;
    };

    const formatStaked = (value) => {
        if (displayMode === 'units') {
            const units = value / unitSize;
            return `${units.toFixed(1)}U`;
        }
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatStakedUnits = (value) => {
        return `${value.toFixed(1)}U`;
    };

    // Calculate daily P/L data (with unit-based calculations when advanced mode is on)
    const dailyPLMap = useMemo(() => {
        const map = {};
        bets.forEach(bet => {
            const dateKey = bet.date;
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : unitSize;
            
            if (!map[dateKey]) {
                map[dateKey] = { 
                    pl: 0, bets: 0, wins: 0, losses: 0, cashedOut: 0, pending: 0, staked: 0, returned: 0,
                    plUnits: 0, stakedUnits: 0, returnedUnits: 0
                };
            }
            map[dateKey].pl += bet.pl;
            map[dateKey].plUnits += bet.pl / betUnitSize;
            map[dateKey].bets++;
            // Exclude bonus bets from staked totals
            if (!bet.bonus_bet) {
                map[dateKey].staked += bet.stake;
                map[dateKey].stakedUnits += bet.stake / betUnitSize;
            }
            
            // Only accumulate returned for settled bets (not pending)
            if (bet.result !== 'pending') {
                map[dateKey].returned += (bet.bonus_bet ? 0 : bet.stake) + bet.pl;
                map[dateKey].returnedUnits += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / betUnitSize;
            }
            
            if (bet.result === 'win') map[dateKey].wins++;
            if (bet.result === 'loss') map[dateKey].losses++;
            if (bet.result === 'cash out') map[dateKey].cashedOut++;
            if (bet.result === 'pending') map[dateKey].pending++;
        });
        // Calculate derived stats for each day
        Object.values(map).forEach(day => {
            day.winRate = (day.wins + day.losses) > 0 ? (day.wins / (day.wins + day.losses)) * 100 : 0;
            day.roi = day.staked > 0 ? (day.pl / day.staked) * 100 : 0;
        });
        return map;
    }, [bets, advancedUnitSettings, getUnitSizeForDate, unitSize]);

    // Calculate monthly P/L data (for yearly view)
    const monthlyPLMap = useMemo(() => {
        const map = {};
        bets.forEach(bet => {
            const betDate = new Date(bet.date);
            const monthKey = `${betDate.getFullYear()}-${String(betDate.getMonth() + 1).padStart(2, '0')}`;
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : unitSize;
            
            if (!map[monthKey]) {
                map[monthKey] = { 
                    pl: 0, bets: 0, wins: 0, losses: 0, cashedOut: 0, pending: 0, staked: 0, returned: 0,
                    plUnits: 0, stakedUnits: 0, returnedUnits: 0
                };
            }
            map[monthKey].pl += bet.pl;
            map[monthKey].plUnits += bet.pl / betUnitSize;
            map[monthKey].bets++;
            // Exclude bonus bets from staked totals
            if (!bet.bonus_bet) {
                map[monthKey].staked += bet.stake;
                map[monthKey].stakedUnits += bet.stake / betUnitSize;
            }
            
            // Only accumulate returned for settled bets (not pending)
            if (bet.result !== 'pending') {
                map[monthKey].returned += (bet.bonus_bet ? 0 : bet.stake) + bet.pl;
                map[monthKey].returnedUnits += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / betUnitSize;
            }
            
            if (bet.result === 'win') map[monthKey].wins++;
            if (bet.result === 'loss') map[monthKey].losses++;
            if (bet.result === 'cash out') map[monthKey].cashedOut++;
            if (bet.result === 'pending') map[monthKey].pending++;
        });
        // Calculate derived stats for each month
        Object.values(map).forEach(month => {
            month.winRate = (month.wins + month.losses) > 0 ? (month.wins / (month.wins + month.losses)) * 100 : 0;
            month.roi = month.staked > 0 ? (month.pl / month.staked) * 100 : 0;
        });
        return map;
    }, [bets, advancedUnitSettings, getUnitSizeForDate, unitSize]);

    // Calculate month stats (for daily view summary)
    const monthStats = useMemo(() => {
        const monthBets = bets.filter(bet => {
            const betDate = new Date(bet.date);
            return betDate.getMonth() === currentMonth && betDate.getFullYear() === currentYear;
        });

        const totalBets = monthBets.length;
        const wins = monthBets.filter(b => b.result === 'win').length;
        const losses = monthBets.filter(b => b.result === 'loss').length;
        const cashedOut = monthBets.filter(b => b.result === 'cash out').length;
        const pl = monthBets.reduce((sum, b) => sum + b.pl, 0);
        // Exclude bonus bets from staked totals
        const staked = monthBets.reduce((sum, b) => sum + (b.bonus_bet ? 0 : b.stake), 0);
        
        // Calculate returned only for settled bets (exclude pending)
        const settledBets = monthBets.filter(b => b.result !== 'pending');
        const returned = settledBets.reduce((sum, b) => sum + (b.bonus_bet ? 0 : b.stake) + b.pl, 0);

        // Calculate unit-based values
        let plUnits = 0, stakedUnits = 0, returnedUnits = 0;
        monthBets.forEach(bet => {
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : unitSize;
            plUnits += bet.pl / betUnitSize;
            // Exclude bonus bets from staked totals
            if (!bet.bonus_bet) {
                stakedUnits += bet.stake / betUnitSize;
            }
            // Only count returned for settled bets
            if (bet.result !== 'pending') {
                returnedUnits += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / betUnitSize;
            }
        });

        return {
            totalBets,
            wins,
            losses,
            cashedOut,
            pl,
            staked,
            returned,
            plUnits,
            stakedUnits,
            returnedUnits,
            roi: staked > 0 ? (pl / staked) * 100 : 0,
            winRate: (wins + losses) > 0 ? (wins / (wins + losses)) * 100 : 0
        };
    }, [bets, currentMonth, currentYear, advancedUnitSettings, getUnitSizeForDate, unitSize]);

    // Calculate year stats (for yearly view summary)
    const yearStats = useMemo(() => {
        const yearBets = bets.filter(bet => {
            const betDate = new Date(bet.date);
            return betDate.getFullYear() === currentYear;
        });

        const totalBets = yearBets.length;
        const wins = yearBets.filter(b => b.result === 'win').length;
        const losses = yearBets.filter(b => b.result === 'loss').length;
        const cashedOut = yearBets.filter(b => b.result === 'cash out').length;
        const pl = yearBets.reduce((sum, b) => sum + b.pl, 0);
        // Exclude bonus bets from staked totals
        const staked = yearBets.reduce((sum, b) => sum + (b.bonus_bet ? 0 : b.stake), 0);
        
        // Calculate returned only for settled bets (exclude pending)
        const settledBets = yearBets.filter(b => b.result !== 'pending');
        const returned = settledBets.reduce((sum, b) => sum + (b.bonus_bet ? 0 : b.stake) + b.pl, 0);

        // Calculate unit-based values
        let plUnits = 0, stakedUnits = 0, returnedUnits = 0;
        yearBets.forEach(bet => {
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : unitSize;
            plUnits += bet.pl / betUnitSize;
            // Exclude bonus bets from staked totals
            if (!bet.bonus_bet) {
                stakedUnits += bet.stake / betUnitSize;
            }
            // Only count returned for settled bets
            if (bet.result !== 'pending') {
                returnedUnits += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / betUnitSize;
            }
        });

        return {
            totalBets,
            wins,
            losses,
            cashedOut,
            pl,
            staked,
            returned,
            plUnits,
            stakedUnits,
            returnedUnits,
            roi: staked > 0 ? (pl / staked) * 100 : 0,
            winRate: (wins + losses) > 0 ? (wins / (wins + losses)) * 100 : 0
        };
    }, [bets, currentYear, advancedUnitSettings, getUnitSizeForDate, unitSize]);

    // Generate yearly calendar months
    const yearlyMonths = useMemo(() => {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return monthNames.map((name, index) => {
            const monthKey = `${currentYear}-${String(index + 1).padStart(2, '0')}`;
            const monthData = monthlyPLMap[monthKey];
            return {
                name,
                monthIndex: index,
                monthKey,
                ...monthData
            };
        });
    }, [currentYear, monthlyPLMap]);

    // Generate calendar days
    const calendarDays = useMemo(() => {
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startDayOfWeek = firstDay.getDay();

        const days = [];

        // Add empty cells for days before the first of the month
        for (let i = 0; i < startDayOfWeek; i++) {
            days.push({ day: null, isEmpty: true });
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayData = dailyPLMap[dateKey];
            
            days.push({
                day,
                dateKey,
                isEmpty: false,
                ...dayData
            });
        }

        return days;
    }, [currentMonth, currentYear, dailyPLMap]);

    // Navigate months
    const goToPrevMonth = () => {
        setSelectedDay(null);
        setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
    };

    const goToNextMonth = () => {
        setSelectedDay(null);
        setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
    };

    // Navigate years
    const goToPrevYear = () => {
        setSelectedMonth(null);
        setCurrentDate(new Date(currentYear - 1, currentMonth, 1));
    };

    const goToNextYear = () => {
        setSelectedMonth(null);
        setCurrentDate(new Date(currentYear + 1, currentMonth, 1));
    };

    // Format for display - use unit values directly when in advanced mode
    const formatPL = (dollarValue, unitValue) => {
        if (dollarValue === undefined || dollarValue === null) return '';
        if (displayMode === 'units') {
            const value = advancedUnitSettings ? unitValue : dollarValue / unitSize;
            const prefix = value >= 0 ? '+' : '-';
            return `${prefix}${Math.abs(value).toFixed(1)}U`;
        }
        const prefix = dollarValue >= 0 ? '+$' : '-$';
        return `${prefix}${Math.abs(dollarValue).toFixed(2)}`;
    };

    // Compact P/L for tight calendar cells on mobile (e.g. +$50, -$1.2k, +2.0U)
    const formatPLCompact = (dollarValue, unitValue) => {
        if (dollarValue === undefined || dollarValue === null) return '';
        if (displayMode === 'units') {
            const value = advancedUnitSettings ? unitValue : dollarValue / unitSize;
            const prefix = value >= 0 ? '+' : '-';
            const abs = Math.abs(value);
            const num = abs >= 100 ? Math.round(abs).toString() : abs.toFixed(1);
            return `${prefix}${num}U`;
        }
        const prefix = dollarValue >= 0 ? '+$' : '-$';
        const abs = Math.abs(dollarValue);
        let num;
        if (abs >= 10000) num = `${(abs / 1000).toFixed(0)}k`;
        else if (abs >= 1000) num = `${(abs / 1000).toFixed(1)}k`;
        else num = Math.round(abs).toString();
        return `${prefix}${num}`;
    };

    const formatStakedValue = (dollarValue, unitValue) => {
        if (displayMode === 'units') {
            const value = advancedUnitSettings ? unitValue : dollarValue / unitSize;
            return `${value.toFixed(1)}U`;
        }
        return `$${dollarValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatCurrency = (value) => {
        return formatValue(value, true);
    };

    const formatCurrencyFull = (value) => {
        return formatValue(value, true);
    };

    const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    // Find max absolute P/L for intensity scaling (daily)
    const maxAbsPL = Math.max(
        ...Object.values(dailyPLMap).map(d => Math.abs(d.pl)),
        1
    );

    // Find max absolute P/L for intensity scaling (monthly)
    const maxAbsMonthlyPL = Math.max(
        ...Object.values(monthlyPLMap).map(d => Math.abs(d.pl)),
        1
    );

    const getDayColor = (pl) => {
        if (pl === undefined) return 'bg-slate-800/30';
        
        const intensity = Math.min(Math.abs(pl) / maxAbsPL, 1);
        
        if (pl > 0) {
            if (intensity > 0.7) return 'bg-emerald-500/60';
            if (intensity > 0.4) return 'bg-emerald-500/40';
            return 'bg-emerald-500/20';
        } else if (pl < 0) {
            if (intensity > 0.7) return 'bg-red-500/60';
            if (intensity > 0.4) return 'bg-red-500/40';
            return 'bg-red-500/20';
        }
        return 'bg-slate-800/30';
    };

    const getMonthColor = (pl) => {
        if (pl === undefined) return 'bg-slate-800/30';
        
        const intensity = Math.min(Math.abs(pl) / maxAbsMonthlyPL, 1);
        
        if (pl > 0) {
            if (intensity > 0.7) return 'bg-emerald-500/60';
            if (intensity > 0.4) return 'bg-emerald-500/40';
            return 'bg-emerald-500/20';
        } else if (pl < 0) {
            if (intensity > 0.7) return 'bg-red-500/60';
            if (intensity > 0.4) return 'bg-red-500/40';
            return 'bg-red-500/20';
        }
        return 'bg-slate-800/30';
    };

    const currentStats = viewMode === 'daily' ? monthStats : yearStats;
    const headerLabel = viewMode === 'daily' ? monthName : `${currentYear}`;
    const headerPL = currentStats.pl;

    return (
        <div className="bg-[#0f172a] rounded border border-slate-700/50 flex flex-col h-full">
            {/* Command Bar Header */}
            <div className="flex items-center justify-between px-3 md:px-4 py-2.5 border-b border-slate-700/50 bg-slate-800/30 rounded-t">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className={`w-1 h-4 ${headerPL >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <h3 className="text-xs md:text-sm font-semibold text-white tracking-tight whitespace-nowrap">Performance Calendar</h3>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex bg-slate-800/50 border border-slate-700/50 rounded p-0.5">
                        <button
                            onClick={() => setViewMode('daily')}
                            className={`px-2 py-1 rounded text-[10px] font-mono font-medium transition-all ${
                                viewMode === 'daily' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-white'
                            }`}
                        >
                            DAILY
                        </button>
                        <button
                            onClick={() => setViewMode('yearly')}
                            className={`px-2 py-1 rounded text-[10px] font-mono font-medium transition-all ${
                                viewMode === 'yearly' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-white'
                            }`}
                        >
                            YEARLY
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-3 md:p-4 flex-1 flex flex-col">
                {/* Period Navigation */}
                <div className="flex items-center justify-between mb-3 bg-slate-800/30 border border-slate-700/30 rounded px-2 py-1.5">
                    <button
                        onClick={viewMode === 'daily' ? goToPrevMonth : goToPrevYear}
                        className="p-1 rounded text-slate-500 hover:text-emerald-400 hover:bg-slate-700/30 transition-all"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <div className="flex items-center gap-2">
                        <span className="text-xs md:text-sm font-mono font-semibold text-white tracking-wide uppercase">{headerLabel}</span>
                        <span className={`text-[11px] font-mono font-bold tabular-nums ${headerPL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatPL(headerPL, currentStats.plUnits)}
                        </span>
                    </div>

                    <button
                        onClick={viewMode === 'daily' ? goToNextMonth : goToNextYear}
                        className="p-1 rounded text-slate-500 hover:text-emerald-400 hover:bg-slate-700/30 transition-all"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                {viewMode === 'daily' ? (
                    <>
                        {/* Day Headers (mobile uses single letter, desktop full short name) */}
                        <div className="grid grid-cols-7 gap-1 mb-1">
                            {[
                                { mobile: 'S', desktop: 'SUN' },
                                { mobile: 'M', desktop: 'MON' },
                                { mobile: 'T', desktop: 'TUE' },
                                { mobile: 'W', desktop: 'WED' },
                                { mobile: 'T', desktop: 'THU' },
                                { mobile: 'F', desktop: 'FRI' },
                                { mobile: 'S', desktop: 'SAT' },
                            ].map((day, idx) => (
                                <div key={idx} className="text-center text-[9px] font-mono font-medium text-slate-600 tracking-widest py-1">
                                    <span className="md:hidden">{day.mobile}</span>
                                    <span className="hidden md:inline">{day.desktop}</span>
                                </div>
                            ))}
                        </div>

                        {/* Calendar Grid (true 7-col calendar on all sizes) */}
                        <div className="grid grid-cols-7 gap-0.5 md:gap-1">
                            {calendarDays.map((day, index) => {
                                const isSelected = selectedDay && day.dateKey && selectedDay.dateKey === day.dateKey;
                                return (
                                <div
                                    key={index}
                                    role={day.bets > 0 ? 'button' : undefined}
                                    tabIndex={day.bets > 0 ? 0 : undefined}
                                    className={`relative aspect-[5/6] md:aspect-[4/3] rounded-sm transition-all duration-150 border ${
                                        day.isEmpty
                                            ? 'bg-transparent border-transparent'
                                            : isSelected
                                                ? `${getDayColor(day.pl)} border-emerald-400/70 ring-1 ring-emerald-400/50`
                                                : `${getDayColor(day.pl)} border-slate-700/30 hover:border-slate-500/70`
                                    } ${day.bets > 0 ? 'cursor-pointer' : 'cursor-default'}`}
                                    onMouseEnter={() => day.bets > 0 && setHoveredDay(day)}
                                    onMouseLeave={() => setHoveredDay(null)}
                                    onClick={() => {
                                        if (day.bets > 0) {
                                            setSelectedDay(prev => prev && prev.dateKey === day.dateKey ? null : day);
                                        }
                                    }}
                                >
                                    {!day.isEmpty && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center px-0.5 md:p-1 overflow-hidden">
                                            <span className={`text-[10px] md:text-[11px] font-mono font-medium leading-tight ${
                                                day.bets > 0 ? 'text-white' : 'text-slate-600'
                                            }`}>
                                                {day.day}
                                            </span>

                                            {day.pl !== undefined && (
                                                <>
                                                    {/* Mobile: compact P/L (e.g. +$50, -$1.2k) */}
                                                    <span className={`md:hidden text-[8px] font-mono font-semibold leading-tight truncate max-w-full tabular-nums ${
                                                        day.pl >= 0 ? 'text-emerald-300' : 'text-red-300'
                                                    }`}>
                                                        {formatPLCompact(day.pl, day.plUnits)}
                                                    </span>
                                                    {/* Desktop: full P/L */}
                                                    <span className={`hidden md:inline text-[10px] font-mono font-semibold leading-tight truncate max-w-full tabular-nums ${
                                                        day.pl >= 0 ? 'text-emerald-300' : 'text-red-300'
                                                    }`}>
                                                        {formatPL(day.pl, day.plUnits)}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {hoveredDay && hoveredDay.dateKey === day.dateKey && (
                                        <div className="hidden md:block absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-slate-900 border border-slate-700/70 rounded shadow-2xl pointer-events-none">
                                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-t-slate-900" />
                                            <div className="px-2.5 py-1.5 border-b border-slate-700/50 bg-slate-800/40">
                                                <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase text-center">
                                                    {new Date(day.dateKey).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                </div>
                                            </div>
                                            <div className="px-2.5 py-2 space-y-1 text-[10px] font-mono">
                                                <div className="flex justify-between">
                                                    <span className="text-slate-600 tracking-widest uppercase">Bets</span>
                                                    <span className="text-white tabular-nums">{day.bets}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-600 tracking-widest uppercase">W·L</span>
                                                    <span className="tabular-nums">
                                                        <span className="text-emerald-400">{day.wins}</span>
                                                        <span className="text-slate-600">·</span>
                                                        <span className="text-red-400">{day.losses}</span>
                                                    </span>
                                                </div>
                                                {day.cashedOut > 0 && (
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-600 tracking-widest uppercase">Cashed</span>
                                                        <span className="text-blue-400 tabular-nums">{day.cashedOut}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between">
                                                    <span className="text-slate-600 tracking-widest uppercase">Win%</span>
                                                    <span className="text-white tabular-nums">{day.winRate.toFixed(1)}%</span>
                                                </div>
                                                <div className="border-t border-slate-700/50 my-1" />
                                                <div className="flex justify-between">
                                                    <span className="text-slate-600 tracking-widest uppercase">Staked</span>
                                                    <span className="text-slate-300 tabular-nums">{formatStakedValue(day.staked, day.stakedUnits)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-600 tracking-widest uppercase">Returned</span>
                                                    <span className="text-slate-300 tabular-nums">{formatStakedValue(day.returned, day.returnedUnits)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-600 tracking-widest uppercase">P/L</span>
                                                    <span className={`font-semibold tabular-nums ${day.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                        {formatPL(day.pl, day.plUnits)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-600 tracking-widest uppercase">ROI</span>
                                                    <span className={`font-semibold tabular-nums ${day.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                        {day.roi >= 0 ? '+' : ''}{day.roi.toFixed(1)}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>

                        {/* Mobile selected-day details panel */}
                        {selectedDay && (
                            <div className="md:hidden mt-2 bg-slate-800/40 border border-slate-700/50 rounded p-2.5">
                                <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-700/50">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-1 h-3.5 ${selectedDay.pl >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                        <span className="text-[11px] font-mono font-semibold text-white tracking-wide uppercase">
                                            {new Date(selectedDay.dateKey).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedDay(null)}
                                        className="p-0.5 text-slate-500 hover:text-white"
                                        aria-label="Close"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[10px] font-mono">
                                    <div className="flex justify-between">
                                        <span className="text-slate-600 tracking-widest uppercase">Bets</span>
                                        <span className="text-white tabular-nums">{selectedDay.bets}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-600 tracking-widest uppercase">W·L</span>
                                        <span className="tabular-nums">
                                            <span className="text-emerald-400">{selectedDay.wins}</span>
                                            <span className="text-slate-600">·</span>
                                            <span className="text-red-400">{selectedDay.losses}</span>
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-600 tracking-widest uppercase">Win%</span>
                                        <span className="text-white tabular-nums">{selectedDay.winRate.toFixed(1)}%</span>
                                    </div>
                                    {selectedDay.cashedOut > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-slate-600 tracking-widest uppercase">Cashed</span>
                                            <span className="text-blue-400 tabular-nums">{selectedDay.cashedOut}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-slate-600 tracking-widest uppercase">Staked</span>
                                        <span className="text-slate-300 tabular-nums">{formatStakedValue(selectedDay.staked, selectedDay.stakedUnits)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-600 tracking-widest uppercase">Returned</span>
                                        <span className="text-slate-300 tabular-nums">{formatStakedValue(selectedDay.returned, selectedDay.returnedUnits)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-600 tracking-widest uppercase">P/L</span>
                                        <span className={`font-semibold tabular-nums ${selectedDay.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatPL(selectedDay.pl, selectedDay.plUnits)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-600 tracking-widest uppercase">ROI</span>
                                        <span className={`font-semibold tabular-nums ${selectedDay.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {selectedDay.roi >= 0 ? '+' : ''}{selectedDay.roi.toFixed(1)}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="grid grid-cols-4 gap-1">
                        {yearlyMonths.map((month, index) => {
                            const isMonthSelected = selectedMonth && selectedMonth.monthKey === month.monthKey;
                            return (
                            <div
                                key={index}
                                role={month.bets > 0 ? 'button' : undefined}
                                tabIndex={month.bets > 0 ? 0 : undefined}
                                className={`relative aspect-[4/3] rounded-sm transition-all duration-150 border ${
                                    isMonthSelected
                                        ? `${getMonthColor(month.pl)} border-emerald-400/70 ring-1 ring-emerald-400/50`
                                        : `${getMonthColor(month.pl)} border-slate-700/30 hover:border-slate-500/70`
                                } ${month.bets > 0 ? 'cursor-pointer' : 'cursor-default'}`}
                                onMouseEnter={() => month.bets > 0 && setHoveredMonth(month)}
                                onMouseLeave={() => setHoveredMonth(null)}
                                onClick={() => {
                                    if (month.bets > 0) {
                                        setSelectedMonth(prev => prev && prev.monthKey === month.monthKey ? null : month);
                                    }
                                }}
                            >
                                <div className="absolute inset-0 flex flex-col items-center justify-center p-1 overflow-hidden">
                                    <span className={`text-[11px] md:text-xs font-mono font-medium leading-tight uppercase tracking-wide ${month.bets > 0 ? 'text-white' : 'text-slate-600'}`}>
                                        {month.name}
                                    </span>
                                    {month.pl !== undefined && (
                                        <span className={`text-[10px] md:text-[11px] font-mono font-semibold leading-tight truncate max-w-full tabular-nums ${month.pl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                                            {formatPL(month.pl, month.plUnits)}
                                        </span>
                                    )}
                                </div>

                                {hoveredMonth && hoveredMonth.monthKey === month.monthKey && (
                                    <div className="hidden md:block absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-slate-900 border border-slate-700/70 rounded shadow-2xl pointer-events-none">
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-t-slate-900" />
                                        <div className="px-2.5 py-1.5 border-b border-slate-700/50 bg-slate-800/40">
                                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase text-center">
                                                {month.name} {currentYear}
                                            </div>
                                        </div>
                                        <div className="px-2.5 py-2 space-y-1 text-[10px] font-mono">
                                            <div className="flex justify-between">
                                                <span className="text-slate-600 tracking-widest uppercase">Bets</span>
                                                <span className="text-white tabular-nums">{month.bets}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-600 tracking-widest uppercase">W·L</span>
                                                <span className="tabular-nums">
                                                    <span className="text-emerald-400">{month.wins}</span>
                                                    <span className="text-slate-600">·</span>
                                                    <span className="text-red-400">{month.losses}</span>
                                                </span>
                                            </div>
                                            {month.cashedOut > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-slate-600 tracking-widest uppercase">Cashed</span>
                                                    <span className="text-blue-400 tabular-nums">{month.cashedOut}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between">
                                                <span className="text-slate-600 tracking-widest uppercase">Win%</span>
                                                <span className="text-white tabular-nums">{month.winRate.toFixed(1)}%</span>
                                            </div>
                                            <div className="border-t border-slate-700/50 my-1" />
                                            <div className="flex justify-between">
                                                <span className="text-slate-600 tracking-widest uppercase">Staked</span>
                                                <span className="text-slate-300 tabular-nums">{formatStakedValue(month.staked, month.stakedUnits)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-600 tracking-widest uppercase">Returned</span>
                                                <span className="text-slate-300 tabular-nums">{formatStakedValue(month.returned, month.returnedUnits)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-600 tracking-widest uppercase">P/L</span>
                                                <span className={`font-semibold tabular-nums ${month.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {formatPL(month.pl, month.plUnits)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-600 tracking-widest uppercase">ROI</span>
                                                <span className={`font-semibold tabular-nums ${month.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {month.roi >= 0 ? '+' : ''}{month.roi.toFixed(1)}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                )}

                {/* Mobile selected-month details panel (yearly view) */}
                {viewMode === 'yearly' && selectedMonth && (
                    <div className="md:hidden mt-2 bg-slate-800/40 border border-slate-700/50 rounded p-2.5">
                        <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-700/50">
                            <div className="flex items-center gap-2">
                                <div className={`w-1 h-3.5 ${selectedMonth.pl >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                <span className="text-[11px] font-mono font-semibold text-white tracking-wide uppercase">
                                    {selectedMonth.name} {currentYear}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelectedMonth(null)}
                                className="p-0.5 text-slate-500 hover:text-white"
                                aria-label="Close"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[10px] font-mono">
                            <div className="flex justify-between">
                                <span className="text-slate-600 tracking-widest uppercase">Bets</span>
                                <span className="text-white tabular-nums">{selectedMonth.bets}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-600 tracking-widest uppercase">W·L</span>
                                <span className="tabular-nums">
                                    <span className="text-emerald-400">{selectedMonth.wins}</span>
                                    <span className="text-slate-600">·</span>
                                    <span className="text-red-400">{selectedMonth.losses}</span>
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-600 tracking-widest uppercase">Win%</span>
                                <span className="text-white tabular-nums">{selectedMonth.winRate.toFixed(1)}%</span>
                            </div>
                            {selectedMonth.cashedOut > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-slate-600 tracking-widest uppercase">Cashed</span>
                                    <span className="text-blue-400 tabular-nums">{selectedMonth.cashedOut}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-slate-600 tracking-widest uppercase">Staked</span>
                                <span className="text-slate-300 tabular-nums">{formatStakedValue(selectedMonth.staked, selectedMonth.stakedUnits)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-600 tracking-widest uppercase">Returned</span>
                                <span className="text-slate-300 tabular-nums">{formatStakedValue(selectedMonth.returned, selectedMonth.returnedUnits)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-600 tracking-widest uppercase">P/L</span>
                                <span className={`font-semibold tabular-nums ${selectedMonth.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {formatPL(selectedMonth.pl, selectedMonth.plUnits)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-600 tracking-widest uppercase">ROI</span>
                                <span className={`font-semibold tabular-nums ${selectedMonth.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {selectedMonth.roi >= 0 ? '+' : ''}{selectedMonth.roi.toFixed(1)}%
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Summary Strip */}
                <div className="mt-4 pt-4 border-t border-slate-700/50 space-y-2">
                    {/* Row 1: Bets · W/L/CO · Win% */}
                    <div className="grid grid-cols-3 gap-2">
                        <div className="bg-slate-800/30 border border-slate-700/30 rounded px-3 py-2.5">
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase mb-1">Bets</div>
                            <div className="text-lg md:text-xl font-mono font-bold text-white tabular-nums leading-none">{currentStats.totalBets}</div>
                        </div>
                        <div className="bg-slate-800/30 border border-slate-700/30 rounded px-3 py-2.5">
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase mb-1">W·L·CO</div>
                            <div className="text-lg md:text-xl font-mono font-bold tabular-nums leading-none">
                                <span className="text-emerald-400">{currentStats.wins}</span>
                                <span className="text-slate-600 mx-0.5">·</span>
                                <span className="text-red-400">{currentStats.losses}</span>
                                <span className="text-slate-600 mx-0.5">·</span>
                                <span className="text-blue-400">{currentStats.cashedOut}</span>
                            </div>
                        </div>
                        <div className="bg-slate-800/30 border border-slate-700/30 rounded px-3 py-2.5">
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase mb-1">Win%</div>
                            <div className="text-lg md:text-xl font-mono font-bold text-white tabular-nums leading-none">{currentStats.winRate.toFixed(1)}<span className="text-sm text-slate-500">%</span></div>
                        </div>
                    </div>

                    {/* Row 2: Staked · Returned */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-slate-800/30 border border-slate-700/30 rounded px-3 py-2.5">
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase mb-1">Staked</div>
                            <div className="text-lg md:text-xl font-mono font-bold text-white tabular-nums leading-none truncate">{formatStakedValue(currentStats.staked, currentStats.stakedUnits)}</div>
                        </div>
                        <div className="bg-slate-800/30 border border-slate-700/30 rounded px-3 py-2.5">
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase mb-1">Returned</div>
                            <div className="text-lg md:text-xl font-mono font-bold text-white tabular-nums leading-none truncate">{formatStakedValue(currentStats.returned, currentStats.returnedUnits)}</div>
                        </div>
                    </div>

                    {/* Row 3: P/L · ROI */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className={`relative bg-slate-800/30 border rounded px-3 py-2.5 ${currentStats.pl >= 0 ? 'border-emerald-500/20' : 'border-red-500/20'}`}>
                            <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${currentStats.pl >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase mb-1">P/L</div>
                            <div className={`text-lg md:text-xl font-mono font-bold tabular-nums leading-none truncate ${currentStats.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {formatPL(currentStats.pl, currentStats.plUnits)}
                            </div>
                        </div>
                        <div className={`relative bg-slate-800/30 border rounded px-3 py-2.5 ${currentStats.roi >= 0 ? 'border-emerald-500/20' : 'border-red-500/20'}`}>
                            <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${currentStats.roi >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase mb-1">ROI</div>
                            <div className={`text-lg md:text-xl font-mono font-bold tabular-nums leading-none ${currentStats.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {currentStats.roi >= 0 ? '+' : ''}{currentStats.roi.toFixed(1)}<span className="text-sm">%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Legend */}
                <div className="mt-2.5 flex items-center justify-center gap-4">
                    <div className="flex items-center gap-1.5">
                        <div className="flex gap-px">
                            <div className="w-2.5 h-2.5 bg-emerald-500/20" />
                            <div className="w-2.5 h-2.5 bg-emerald-500/40" />
                            <div className="w-2.5 h-2.5 bg-emerald-500/60" />
                        </div>
                        <span className="text-[9px] font-mono text-slate-600 tracking-widest uppercase">Profit</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="flex gap-px">
                            <div className="w-2.5 h-2.5 bg-red-500/20" />
                            <div className="w-2.5 h-2.5 bg-red-500/40" />
                            <div className="w-2.5 h-2.5 bg-red-500/60" />
                        </div>
                        <span className="text-[9px] font-mono text-slate-600 tracking-widest uppercase">Loss</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MonthlyCalendar;
